<template>
  <div
    id="custom_table"
    ref="tableContainer"
    class="custom_table"
  >
    <el-table
      v-if="props.columns.length > 0"
      ref="custom_table"
      class="my-infinite-table"
      :data="props.data"
      :height="tableHeight"
      style="width: 100%"
      :highlight-current-row="false"
      :default-sort="props.serverSort ? { prop: null, order: null } : undefined"
      empty-text="Sin datos"
      border
      @sort-change="handleSortChange"
      @selection-change="handleSelectionChange"
    >
      <el-table-column
        v-if="props.selecting"
        type="selection"
        width="55"
      />

      <el-table-column
        v-for="(column, index) in props.columns.filter((c) => c.isVisible === true)"
        :key="`col${index}`"
        :prop="column.field"
        :label="column.label"
        :width="column.width === 0 ? null : column.width"
        :sortable="getSortableType(column)"
        show-overflow-tooltip
      >
        <template #default="scope">
          <template v-if="column.slot">
            <slot
              :name="column.slot"
              :row="scope.row"
              :value="getNestedValue(scope.row, column.field)"
            >
              {{ formatCell(getNestedValue(scope.row, column.field), column) }}
            </slot>
          </template>
          <template v-else>
            {{ formatCell(getNestedValue(scope.row, column.field), column) }}
          </template>
        </template>
      </el-table-column>

      <el-table-column
        v-if="!props.selecting"
        label="Acciones"
        width="150"
        align="center"
        :fixed="fixedActions || 'right'"
      >
        <template #default="scope">
          <el-button
            v-if="props.showDetailButton"
            :icon="IconVisibility"
            @click.prevent="showRow(scope.row)"
          ></el-button>
          <el-button
            v-if="props.showEditButton"
            :icon="IconEdit"
            @click.prevent="editRow(scope.row)"
          ></el-button>
          <slot
            name="custom-actions"
            :row="scope.row"
          ></slot>
        </template>
      </el-table-column>

      <template #append>
        <InfiniteLoading
          ref="infiniteLoading"
          :key="identifier"
          :distance="200"
          target=".my-infinite-table .el-scrollbar__wrap"
          :firstload="firstLoad"
          @infinite="infiniteHandler"
        >
          <template #spinner><div class="infinite-state">Cargando datos...</div></template>
          <template #complete><div class="infinite-state">No hay mas datos</div></template>
          <template #error><div class="infinite-state">Hubo un error...</div></template>
        </InfiniteLoading>
      </template>
    </el-table>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  watch,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useRouter } from 'vue-router';
import { useWidthContentPage } from '@/stores/widthContentPage';
import { useMessageError } from '@/shared/composables/message_error.js';
import IconVisibility from '~icons/mdi/visibility';
import InfiniteLoading from 'v3-infinite-loading';
import 'v3-infinite-loading/lib/style.css';
import IconEdit from '~icons/mdi/edit';
import getDate from '@/shared/components/utils.js';
import { formatNumberLocale } from '@/shared/utils/locale_number_utils.js';

const router = useRouter();
const widthPageStore = useWidthContentPage();
const infiniteLoading = ref(null);
const tableContainer = ref(null);

//local state
const identifier = ref(Date.now());
const tableHeight = ref(200);
const isLoading = ref(true);
let resizeObserver = null;
let windowResizeHandler = null;

const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, prop) => current?.[prop], obj);
};

const formatCell = (value, column) => {
  if (column.isDate) {
    return getDate('dd/mm/yyyy', value);
  }
  if (column.isNumber) {
    const strValue = String(value);
    if (strValue.includes(', ')) {
      const parts = strValue.split(', ');
      const formatted = parts.map((part) => {
        const num = Number(part);
        return !isNaN(num) ? formatNumberLocale(num, column.precision) : part;
      });
      return formatted.join(', ');
    }
    const num = Number(value);
    if (!isNaN(num)) {
      return formatNumberLocale(num, column.precision);
    }
  }
  return value;
};

//props
const props = defineProps({
  data: Array,
  columns: Array,
  pathDetail: String,
  pathEdit: String,
  paramId: String,
  requestApi: {
    type: Function,
    required: true,
  },
  requestArgs: {
    type: Object,
  },
  showDetailButton: {
    type: Boolean,
    default: true,
  },
  showEditButton: {
    type: Boolean,
    default: true,
  },
  height: {
    type: Number,
    default: 0,
  },
  serverSort: {
    type: Boolean,
    default: false,
  },
  firstLoad: {
    type: Boolean,
    default: false,
  },
  selecting: {
    type: Boolean,
    default: false,
  },
  fixedActions: {
    type: [String, Boolean],
    default: false,
  },
});

// emits
const emit = defineEmits(['sort-change', 'selection-change']);

// methods
const getSortableType = (column) => {
  if (!column.sortable) return false;
  return props.serverSort ? 'custom' : true;
};

const handleSortChange = (sortData) => {
  // Solo emitir el evento si es ordenamiento del servidor
  if (props.serverSort) {
    const { prop, order } = sortData;
    emit('sort-change', prop, order);
  }
};

const handleSelectionChange = (selection) => {
  emit('selection-change', selection);
};

const showRow = (row) => {
  if (row) {
    if (row && row.Codigo) {
      router.push(`${props.pathDetail}/${row.Codigo}`);
    }
  }
};

const editRow = (row) => {
  if (row) {
    if (row && row.Codigo) {
      router.push(`${props.pathEdit}/${row.Codigo}`);
    }
  }
};

const infiniteHandler = async ($state) => {
  isLoading.value = true;
  try {
    const result = await props.requestApi(props.requestArgs);
    if (result === true) {
      await nextTick();
      $state && $state.loaded();
    } else {
      $state && $state.complete();
    }
  } catch (err) {
    $state?.error?.();
    useMessageError().open(err);
  } finally {
    isLoading.value = false;
  }
};

// Función  para calcular altura
// Función para calcular altura dinámicamente de forma robusta
const calculateTableHeight = () => {
  if (props.height && props.height > 0) {
    tableHeight.value = props.height;
    return;
  }

  nextTick(() => {
    try {
      if (!tableContainer.value) return;

      const windowHeight = window.innerHeight;

      // getBoundingClientRect().top nos da la distancia exacta en píxeles
      // desde el borde superior del monitor hasta donde empieza nuestra tabla.
      // Esto ignora filtros, breadcrumbs o botones: simplemente sabe dónde arranca la tabla.
      const tableTopPosition = tableContainer.value.getBoundingClientRect().top;

      // El buffer inferior cubre el padding de tu .custom_table (20px),
      // el texto de "Cargando datos" del scroll infinito y un margen de respiro visual.
      const BOTTOM_BUFFER = 80;

      const availableHeight = windowHeight - tableTopPosition - BOTTOM_BUFFER;

      // Altura mínima por si la pantalla es excesivamente pequeña
      const MIN_HEIGHT = 200;
      const newHeight = Math.max(availableHeight, MIN_HEIGHT);

      if (newHeight !== tableHeight.value) {
        tableHeight.value = newHeight;
      }
    } catch (error) {
      console.error('Error calculando altura de tabla:', error);
    }
  });
};

const calculateTableWidth = () => {
  const totalWidth = props.columns.reduce((sum, c) => sum + +c.width, 0) + 150;
  widthPageStore.setWidthPage(totalWidth);
};

const setupResizeObserver = () => {
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      calculateTableHeight();
    });

    const mainContainer = document.querySelector('#main_container');
    if (mainContainer) {
      resizeObserver.observe(mainContainer);
    }

    if (tableContainer.value) {
      resizeObserver.observe(tableContainer.value);
    }
  }
};

const handleWindowResize = () => {
  setTimeout(calculateTableHeight, 100);
};

// lifecycle
onMounted(async () => {
  calculateTableWidth();
  await nextTick();
  setTimeout(() => {
    calculateTableHeight();
    setupResizeObserver();
  }, 120);

  windowResizeHandler = handleWindowResize;
  window.addEventListener('resize', windowResizeHandler);
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }

  if (windowResizeHandler) {
    window.removeEventListener('resize', windowResizeHandler);
  }
});

watch(
  () => props.requestArgs,
  () => {
    identifier.value = Date.now();
    if (infiniteLoading.value && infiniteLoading.value.stateChanger) {
      infiniteLoading.value.stateChanger.reset();
    }
  },
  { deep: true },
);

watch(
  () => props.columns,
  () => {
    calculateTableWidth();
    nextTick(() => calculateTableHeight());
  },
  { deep: true },
);
</script>

<style scoped>
.custom_table {
  flex: 1;
  width: 100%;
  min-height: 0;
  padding: 20px;
  background-color: white;
  align-items: flex-start;
}

/* Ya no necesitamos la clase .table-scroll, pero agregamos esto para que los mensajes del spinner se vean bien */
.infinite-state {
  padding: 15px;
  text-align: center;
  color: #909399;
  font-size: 14px;
}
</style>
